import { clamp } from '../math.ts';
import { createDefaultEnvironment } from './environment.ts';
import { createDefaultLook } from './look.ts';
import {
  DEFAULT_MAP_HEIGHT,
  DEFAULT_MAP_WIDTH,
  MAX_ELEVATION,
  MAX_LAPS,
  MAX_MAP_SIZE,
  MAX_WATER_DEPTH,
  MIN_ELEVATION,
  MIN_LAPS,
  MIN_MAP_SIZE,
  MIN_WATER_DEPTH,
  type MapBounds,
  type MapDocument,
  type Vec2,
} from './types.ts';
import { emptyMapMeta } from './meta.ts';
import { CURRENT_MAP_VERSION } from './version.ts';

/** Metres above the immediate support (or water). Clamped everywhere it is set. */
export function clampElevation(value: number): number {
  return clamp(value, MIN_ELEVATION, MAX_ELEVATION);
}

/** Metres below the surface for a shallow-water bed. */
export function clampDepth(value: number): number {
  return clamp(value, MIN_WATER_DEPTH, MAX_WATER_DEPTH);
}

/**
 * The editable range offered in the editor. It is deliberately not enforced on the document:
 * tests and hand-authored maps may use any positive size.
 */
export function clampMapSize(value: number): number {
  return Math.round(clamp(value, MIN_MAP_SIZE, MAX_MAP_SIZE));
}

/** Lap count the race and the editor both honor. Missing or junk values are one lap. */
export function mapLapCount(laps: number): number {
  if (!Number.isFinite(laps)) {
    return MIN_LAPS;
  }

  return Math.min(MAX_LAPS, Math.max(MIN_LAPS, Math.round(laps)));
}

export function setLaps(map: MapDocument, laps: number): void {
  map.laps = mapLapCount(laps);
}

export function createEmptyMap(bounds?: Partial<MapBounds>): MapDocument {
  return {
    version: CURRENT_MAP_VERSION,
    name: '',
    meta: emptyMapMeta(),
    bounds: {
      width: bounds?.width ?? DEFAULT_MAP_WIDTH,
      height: bounds?.height ?? DEFAULT_MAP_HEIGHT,
    },
    environment: createDefaultEnvironment(),
    look: createDefaultLook(),
    islands: [],
    rocks: [],
    shallowWater: [],
    checkpoints: [],
    laps: MIN_LAPS,
    routes: [],
    startArea: null,
    parkArea: null,
    nextIslandId: 1,
    nextRockId: 1,
    nextShallowWaterId: 1,
    nextCheckpointId: 1,
    nextRouteId: 1,
  };
}

/**
 * Resizing is destructive on purpose: anything left outside the new bounds is pulled to the edge
 * rather than silently kept out of play, so what the author sees is what the racer gets.
 */
export function setMapSize(map: MapDocument, size: Partial<MapBounds>): void {
  const bounds: MapBounds = {
    width: clampMapSize(size.width ?? map.bounds.width),
    height: clampMapSize(size.height ?? map.bounds.height),
  };

  map.bounds = bounds;

  for (const ring of [
    ...map.islands,
    ...map.rocks,
    ...map.shallowWater,
    ...(map.startArea ? [map.startArea] : []),
    ...(map.parkArea ? [map.parkArea] : []),
  ]) {
    ring.vertices = ring.vertices.map((vertex) => clampToBounds(vertex, bounds));

    for (const edge of ring.edges) {
      if (edge.bend) {
        edge.bend = clampToBounds(edge.bend, bounds);
      }
    }
  }

  for (const checkpoint of map.checkpoints) {
    checkpoint.a = clampToBounds(checkpoint.a, bounds);
    checkpoint.b = clampToBounds(checkpoint.b, bounds);
  }

  for (const route of map.routes) {
    route.points = route.points.map((point) => clampToBounds(point, bounds));
  }

  for (const island of map.islands) {
    for (const decoration of island.decorations) {
      const clamped = clampToBounds(decoration, bounds);
      decoration.x = clamped.x;
      decoration.y = clamped.y;
    }
  }
}

export function clonePoint(point: Vec2): Vec2 {
  return { x: point.x, y: point.y };
}

export function clampToBounds(point: Vec2, bounds: MapBounds): Vec2 {
  return {
    x: clamp(point.x, 0, bounds.width),
    y: clamp(point.y, 0, bounds.height),
  };
}

export function distanceSquared(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

export function distance(a: Vec2, b: Vec2): number {
  return Math.sqrt(distanceSquared(a, b));
}

export function isNear(a: Vec2, b: Vec2, radius: number): boolean {
  return distanceSquared(a, b) <= radius * radius;
}

export function midpoint(a: Vec2, b: Vec2): Vec2 {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export function closestPointOnSegment(point: Vec2, a: Vec2, b: Vec2): Vec2 {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lengthSq = abx * abx + aby * aby;

  if (lengthSq <= 1e-9) {
    return clonePoint(a);
  }

  const t = clamp(((point.x - a.x) * abx + (point.y - a.y) * aby) / lengthSq, 0, 1);
  return { x: a.x + abx * t, y: a.y + aby * t };
}

export function movePoint(from: Vec2, toward: Vec2, dist: number): Vec2 {
  const dx = toward.x - from.x;
  const dy = toward.y - from.y;
  const len = Math.hypot(dx, dy);

  if (len <= 1e-9 || dist <= 0) {
    return clonePoint(from);
  }

  const t = dist / len;
  return { x: from.x + dx * t, y: from.y + dy * t };
}

export function quadraticBezierPoint(p0: Vec2, control: Vec2, p1: Vec2, t: number): Vec2 {
  const mt = 1 - t;
  const a = mt * mt;
  const b = 2 * mt * t;
  const c = t * t;
  return {
    x: a * p0.x + b * control.x + c * p1.x,
    y: a * p0.y + b * control.y + c * p1.y,
  };
}
